import type { ClientOrRelativesRequest, ClientOrRelativesResponse } from 'apiConnector/secondary-market/shared/clients';
import type { ReloadFormResponse } from 'apiConnector/secondary-market/shared/form';
import type { ReferentialFormResponse } from 'apiConnector/secondary-market/shared/referential';
import type { SettlementTypesResponse } from 'apiConnector/secondary-market/shared/settlement';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import type { HttpErrorKind } from 'utils/errors/errors';

export const secondaryMarketInputFormActionsFactory = <
  ChildForm extends Form,
  ActionType extends OrderActionType,
  ReloadRequest,
  Referential extends ReferentialFormResponse,
  PersistedTab,
>(
  actionType: ActionType,
) => ({
  formFieldChanged(
    tabId: string,
    field: keyof ChildForm,
    value: ChildForm[keyof ChildForm],
    trackChanges: boolean = true,
  ) {
    return {
      type: `SECONDARY_MARKET_${actionType}_FORM_FIELD_CHANGED` as const,
      tabId,
      field,
      value,
      trackChanges,
    };
  },
  tabContentPersistenceRequested(tabId: string, workspace: SecondaryWorkspaceType, tabContent: PersistedTab) {
    return {
      type: `SECONDARY_MARKET_${actionType}_TAB_CONTENT_PERSISTENCE_REQUESTED` as const,
      tabId,
      workspace,
      tabContent,
    };
  },
  tabContentPersisted(tabId: string, workspace: SecondaryWorkspaceType) {
    return {
      type: `SECONDARY_MARKET_${actionType}_TAB_CONTENT_PERSISTED` as const,
      workspace,
      tabId,
    };
  },
  reloadFormFetchRequested(tabId: string, workspace: SecondaryWorkspaceType, request: ReloadRequest) {
    return { type: `SECONDARY_MARKET_${actionType}_RELOAD_FORM_FETCH_REQUESTED` as const, tabId, workspace, request };
  },

  reloadFormFetched(tabId: string, workspace: SecondaryWorkspaceType, response: ReloadFormResponse<Referential>) {
    return { type: `SECONDARY_MARKET_${actionType}_RELOAD_FORM_FETCHED` as const, tabId, workspace, response };
  },
  reloadFormFetchFailed(tabId: string, workspace: SecondaryWorkspaceType, message?: string, errorKind?: HttpErrorKind) {
    return {
      type: `SECONDARY_MARKET_${actionType}_RELOAD_FORM_FETCH_FAILED` as const,
      workspace,
      tabId,
      message,
      errorKind,
    };
  },

  clientRequested(tabId: string, request: ClientOrRelativesRequest) {
    return { type: `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FETCH_REQUESTED` as const, tabId, request };
  },
  clientReceived(tabId: string, response: ClientOrRelativesResponse) {
    return { type: `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_RECEIVED` as const, tabId, response };
  },
  clientRequestFailed(tabId: string, message: string) {
    return { type: `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FAILED` as const, tabId, message };
  },
  custodianRequested(tabId: string, bcsProductKey: BcsProductKey, client: ISalesCounterpart, custodianBdrId: string) {
    return {
      type: `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_FETCH_REQUESTED` as const,
      tabId,
      bcsProductKey,
      client,
      custodianBdrId,
    };
  },

  custodianReceived(tabId: string, response: SettlementTypesResponse) {
    return { type: `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_RECEIVED` as const, tabId, response };
  },
  custodianRequestFailed(tabId: string, message?: string) {
    return { type: `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_FAILED` as const, tabId, message };
  },

  settlementDateRequested(
    tabId: string,
    bcsProductKey: BcsProductKey,
    counterpartyEliotId: string | undefined,
    clientType: ClientType | undefined,
  ) {
    return {
      type: `SECONDARY_MARKET_${actionType}_SETTLEMENT_DATE_REQUESTED` as const,
      tabId,
      bcsProductKey,
      counterpartyEliotId,
      clientType,
    };
  },

  settlementDateReceived(tabId: string, settlementDate: string) {
    return { type: `SECONDARY_MARKET_${actionType}_SETTLEMENT_DATE_RECEIVED` as const, tabId, settlementDate };
  },
  settlementDateRequestFailed(tabId: string, bcsKey: BcsProductKey, errorMessage: string) {
    return { type: `SECONDARY_MARKET_${actionType}_SETTLEMENT_DATE_FAILED` as const, tabId, bcsKey, errorMessage };
  },

  resetForm(tabId: string) {
    return { type: `SECONDARY_MARKET_${actionType}_FORM_RESET` as const, tabId };
  },
});
